/**
 * @file RequirePermission.tsx
 * @description Frontend-зеркало серверного `@RequirePermission()` (phase04 шаг 5).
 *
 * Скрывает дочерний контент, если у активного контекста нет указанных
 * permission'ов. Поведение:
 * - 'redirect' (по умолчанию) — редирект на /403; используется как route guard.
 * - 'hide' — возвращает null, удобно для пунктов меню/кнопок.
 *
 * Это удобство для UX (не показывать пользователю недоступное), а не
 * замена серверной проверке — API всё равно вернёт 403, если бэкенд-guard
 * не дал. AND-семантика как на сервере.
 */

import type { ReactNode } from "react";
import { Navigate, Outlet } from "react-router";

import { useCurrentContext } from "../../auth/hooks";

interface RequirePermissionProps {
  permissions: string[];
  /** Хотя бы одно из прав (сверх `permissions`): «Сайт → SEO» — контент или настройки (Р3в). */
  anyOf?: string[];
  fallback?: "redirect" | "hide";
  children?: ReactNode;
}

export function RequirePermission({
  permissions,
  anyOf,
  fallback = "redirect",
  children,
}: RequirePermissionProps) {
  const ctx = useCurrentContext();
  const allowed =
    !!ctx &&
    permissions.every((p) => ctx.role.permissions.includes(p)) &&
    (!anyOf || anyOf.some((p) => ctx.role.permissions.includes(p)));

  if (allowed) {
    return <>{children ?? <Outlet />}</>;
  }
  if (fallback === "hide") return null;
  return <Navigate to="/403" replace />;
}
